// Test Page for Phase 1: Core Services
// Trang test cho các services: Study History, Quiz History, Achievement

import { useState, useEffect } from 'react';
import { phase1Test } from '../services/studyHistory/__tests__/phase1-integration-test';

export default function TestPhase1() {
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [dbEnabled, setDbEnabled] = useState(false);

  // Check if Learning History DB is enabled
  useEffect(() => {
    const enabled = import.meta.env.VITE_ENABLE_LEARNING_HISTORY_DB === 'true';
    setDbEnabled(enabled);
    
    if (!enabled) {
      setLogs(prev => [...prev, 
        '⚠️ WARNING: Learning History Database is DISABLED',
        '📝 To enable it for testing:',
        '1. Open .env.local file',
        '2. Add: VITE_ENABLE_LEARNING_HISTORY_DB=true',
        '3. Restart dev server (npm run dev)',
        '4. Refresh this page',
        ''
      ]);
    }
  }, []);

  // Capture console logs
  useEffect(() => {
    const originalLog = console.log;
    const originalError = console.error;

    console.log = (...args: any[]) => {
      setLogs(prev => [...prev, args.join(' ')]);
      originalLog(...args);
    };

    console.error = (...args: any[]) => {
      setLogs(prev => [...prev, `❌ ${args.join(' ')}`]);
      originalError(...args);
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
    };
  }, []);

  const runTests = async () => {
    if (!dbEnabled) {
      alert('⚠️ Learning History Database chưa được enable!\n\nVui lòng:\n1. Mở file .env.local\n2. Thêm dòng: VITE_ENABLE_LEARNING_HISTORY_DB=true\n3. Restart dev server\n4. Refresh page');
      return;
    }

    setTesting(true);
    setLogs([]);
    setTestResults(null);

    try {
      // Enable test mode
      (window as any).__RUN_PHASE1_TEST__ = true;

      // Run tests
      await phase1Test.runAllTests();

      setTestResults({
        success: true,
        message: 'All tests completed!'
      });
    } catch (error: any) {
      setTestResults({
        success: false,
        message: error.message || 'Test failed',
        error
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                🧪 Phase 1 Integration Test
              </h1>
              <p className="text-gray-600">
                Test Core Services: StudyHistory, QuizHistory, Achievement
              </p>
            </div>
            <button
              onClick={runTests}
              disabled={testing || !dbEnabled}
              className={`px-8 py-4 rounded-lg font-semibold text-white text-lg
                ${testing || !dbEnabled
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
                }
                transition-all duration-200 shadow-lg hover:shadow-xl
              `}
            >
              {testing ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Running Tests...
                </span>
              ) : !dbEnabled ? (
                '🔒 DB Not Enabled'
              ) : (
                '▶️ Run All Tests'
              )}
            </button>
          </div>

          {/* Database Status Indicator */}
          <div className={`flex items-center gap-3 p-4 rounded-lg ${
            dbEnabled 
              ? 'bg-green-50 border-2 border-green-200' 
              : 'bg-yellow-50 border-2 border-yellow-300'
          }`}>
            <span className="text-2xl">{dbEnabled ? '✅' : '⚠️'}</span>
            <div className="flex-1">
              <p className={`font-semibold ${dbEnabled ? 'text-green-800' : 'text-yellow-800'}`}>
                {dbEnabled ? 'Learning History Database: ENABLED' : 'Learning History Database: DISABLED'}
              </p>
              {!dbEnabled && (
                <p className="text-sm text-yellow-700 mt-1">
                  Authentication and normal app features are working. 
                  To test Phase 1, enable the database in .env.local
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Test Results Summary */}
        {testResults && (
          <div className={`rounded-xl shadow-lg p-6 mb-6 ${
            testResults.success 
              ? 'bg-green-50 border-2 border-green-200' 
              : 'bg-red-50 border-2 border-red-200'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">
                {testResults.success ? '✅' : '❌'}
              </span>
              <div>
                <h2 className={`text-xl font-bold ${
                  testResults.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {testResults.success ? 'Tests Passed!' : 'Tests Failed'}
                </h2>
                <p className={testResults.success ? 'text-green-600' : 'text-red-600'}>
                  {testResults.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Test Logs */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              📋 Test Console Logs
            </h2>
            <button
              onClick={() => setLogs([])}
              className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              Clear Logs
            </button>
          </div>
          
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm overflow-y-auto"
               style={{ maxHeight: '600px' }}>
            {logs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                No logs yet. Click "Run All Tests" to start testing.
              </div>
            ) : (
              logs.map((log, index) => (
                <div 
                  key={index} 
                  className={`py-1 ${
                    log.includes('✅') ? 'text-green-400' :
                    log.includes('❌') ? 'text-red-400' :
                    log.includes('🎉') ? 'text-yellow-400' :
                    log.includes('📊') ? 'text-blue-400' :
                    log.includes('🏆') ? 'text-purple-400' :
                    'text-gray-300'
                  }`}
                >
                  {log}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-4xl mb-3">📚</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Study History Service
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ Start/Update/Complete Lesson</li>
              <li>✓ Bookmark & Notes</li>
              <li>✓ Session Tracking</li>
              <li>✓ Statistics</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-4xl mb-3">📝</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Quiz History Service
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ Save Quiz Results</li>
              <li>✓ Attempt Tracking</li>
              <li>✓ Filters & Sorting</li>
              <li>✓ Trend Analysis</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-4xl mb-3">🏆</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Achievement Service
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ 10+ Achievement Types</li>
              <li>✓ Auto Check & Award</li>
              <li>✓ Progress Tracking</li>
              <li>✓ Gamification</li>
            </ul>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mt-6">
          <h3 className="text-lg font-bold text-blue-800 mb-3">
            📖 Testing Instructions
          </h3>
          <div className="text-blue-700 space-y-2 text-sm">
            <p>1. Click "Run All Tests" button to start the test suite</p>
            <p>2. Watch the console logs for detailed test progress</p>
            <p>3. Check the test results summary at the top</p>
            <p>4. All tests should pass with green checkmarks ✅</p>
            <p>5. If any test fails, review the error messages in logs</p>
          </div>
        </div>
      </div>
    </div>
  );
}

